import { useQuery } from "@tanstack/react-query";
import { CalendarDays, GraduationCap, Link2, RefreshCw, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { useApiClient } from "../../api/ApiClientContext.js";

export interface SignInScreenProps {
  /** Что не так с текущей сессией: подпись над формой. `null` — обычный вход. */
  readonly reason?: "unauthenticated" | "expired" | "server" | null;
  /** Отказ, случившийся до формы: неудачный вход через Telegram, просроченный initData. */
  readonly errorMessage?: string | null;
  readonly onRetry?: () => void;
}

/**
 * Один экран входа на все поверхности.
 *
 * Три пути, каждый со своей проверяющей стороной: логин и пароль (менеджер и админ), Telegram
 * (инструктор) и вход из карточки amoCRM (менеджер, у которого amo уже открыт). Раньше вход был
 * размазан по экрану ошибки менеджера, панели инструктора и форме с выбором роли — то есть тем же
 * паролем можно было войти кем угодно.
 *
 * Внутри Telegram этот экран не показывается: там сессию подтверждает сам Mini App, см.
 * `MiniAppAuthStatus`.
 */
export function SignInScreen(props: SignInScreenProps) {
  const api = useApiClient();
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [state, setState] = useState<"idle" | "pending" | "error">("idle");
  const [message, setMessage] = useState<string | null>(null);
  const [telegramPending, setTelegramPending] = useState(false);

  const methodsQuery = useQuery({
    queryKey: ["auth-methods"],
    queryFn: () => api.getAuthMethods(),
    staleTime: 60_000,
  });
  const methods = methodsQuery.data;
  const canPassword = methods?.password ?? false;
  const telegramOidc = methods?.telegram.available ?? false;
  const telegramBot = methods?.telegram.botUsername ?? null;
  const amoWidget = methods?.amoWidget ?? false;
  // Отказ до формы и отказ формы делят одно место: одновременно они не случаются, а два
  // разных красных блока на экране входа читаются как две разные поломки.
  const failure = message ?? props.errorMessage ?? null;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (state === "pending" || login.trim() === "" || password === "") return;
    setState("pending");
    setMessage(null);
    try {
      const result = await api.loginWithPassword(login.trim(), password);
      // Полная перезагрузка: поверхность выбирается по адресу, и роль решает, какая нужна.
      if (typeof window !== "undefined") {
        window.location.assign(result.role === "admin" ? "/admin" : "/");
      }
    } catch (error) {
      setState("error");
      setMessage(error instanceof Error ? error.message : "Не удалось войти");
    }
  };

  const startTelegram = async () => {
    if (telegramPending) return;
    setTelegramPending(true);
    setMessage(null);
    try {
      const started = await api.startTelegramOidc();
      // Уходим на oauth.telegram.org: адрес возврата задан на сервере, вернёмся уже в кабинет.
      window.location.assign(started.authorizationUrl);
    } catch (error) {
      setTelegramPending(false);
      setMessage(error instanceof Error ? error.message : "Не удалось начать вход через Telegram");
    }
  };

  return (
    <main
      className="relative flex min-h-[100dvh] items-center overflow-hidden bg-pad-100 px-4 py-8 sm:px-6 lg:px-8"
      data-testid="sign-in-screen"
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(circle_at_20%_0%,rgba(14,129,246,0.18),transparent_58%)]"
        aria-hidden="true"
      />
      <div className="relative mx-auto grid w-full max-w-5xl overflow-hidden rounded-3xl border border-white/80 bg-white/90 shadow-2xl backdrop-blur-sm lg:grid-cols-[1.08fr_0.92fr]">
        <section className="pwz-chrome relative flex flex-col justify-between gap-10 overflow-hidden p-6 sm:p-9 lg:p-12">
          <div
            className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-hivis-500/20 blur-3xl"
            aria-hidden="true"
          />
          <div className="relative">
            <div className="flex items-center gap-3">
              <span className="flex size-11 items-center justify-center rounded-2xl bg-hivis-500 text-white shadow-lg">
                <CalendarDays size={22} aria-hidden="true" />
              </span>
              <div>
                <p className="pwz-eyebrow">PWZ Moto Academy</p>
                <p className="font-semibold text-white">Управление обучением</p>
              </div>
            </div>
            <h1 className="mt-12 max-w-lg text-balance font-extrabold text-3xl text-white leading-[1.08] tracking-[-0.035em] sm:text-4xl">
              Расписание без лишних переключений
            </h1>
            <p className="mt-4 max-w-md text-pretty text-base text-ink-200 leading-relaxed">
              Сделка остаётся в amoCRM, а уроки, назначения и история изменений — в одном рабочем
              календаре PWZ.
            </p>
          </div>
          <ul className="relative grid gap-3 text-sm text-ink-100">
            <li className="flex items-center gap-3">
              <Link2 size={18} className="text-hivis-400" aria-hidden="true" />
              Контекст клиента и курса приходит из сделки
            </li>
            <li className="flex items-center gap-3">
              <ShieldCheck size={18} className="text-hivis-400" aria-hidden="true" />
              Доступ проверяется по учётной записи, Telegram или amoCRM
            </li>
          </ul>
        </section>

        <section className="flex flex-col justify-center p-5 sm:p-8 lg:p-10">
          <div>
            <p className="pwz-eyebrow">Вход в систему</p>
            <h2 className="pwz-heading mt-2 text-2xl">
              {props.reason === "expired" ? "Сессия закончилась" : "Продолжить работу"}
            </h2>
            <p className="mt-2 text-sm text-ink-600">
              {props.reason === "server"
                ? "Сервер не ответил на проверку сессии. Повторите запрос или войдите заново."
                : "Менеджер и администратор входят по логину, инструктор — через Telegram."}
            </p>
          </div>

          {props.reason === "server" && props.onRetry ? (
            <div
              className="mt-5 rounded-xl border border-danger-500/20 bg-danger-100 p-3.5"
              role="alert"
              data-testid="sign-in-server-error"
            >
              <p className="font-semibold text-ink-900 text-sm">Сервер временно недоступен</p>
              <button
                type="button"
                className="mt-2 inline-flex min-h-11 items-center gap-2 rounded-lg bg-white px-2.5 font-semibold text-primary text-sm hover:bg-white/80"
                data-testid="error-retry"
                onClick={props.onRetry}
              >
                <RefreshCw size={16} aria-hidden="true" />
                Повторить
              </button>
            </div>
          ) : null}

          {canPassword ? (
            <form
              className="mt-6 flex flex-col gap-3"
              onSubmit={submit}
              data-testid="password-form"
            >
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Логин</span>
                <input
                  className="pwz-field"
                  id="pwz-login"
                  name="login"
                  autoComplete="username"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  data-testid="login-name"
                />
              </label>
              <label className="grid gap-1.5 text-sm">
                <span className="font-medium text-ink-700">Пароль</span>
                <input
                  className="pwz-field"
                  id="pwz-password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  data-testid="login-password"
                />
              </label>
              <button
                type="submit"
                className="pwz-btn pwz-btn-primary"
                disabled={state === "pending" || login.trim() === "" || password === ""}
                data-testid="login-submit"
              >
                {state === "pending" ? "Входим…" : "Войти"}
              </button>
            </form>
          ) : (
            <p
              className="mt-6 rounded-xl bg-pad-100 p-3.5 text-sm text-ink-700"
              data-testid="no-accounts-hint"
            >
              Учётных записей пока нет. Первую создаёт администратор на сервере командой
              <span className="pwz-readout"> create-user</span>, дальше учётки заводятся в админке.
            </p>
          )}

          {failure ? (
            <p
              role="alert"
              className="mt-3 break-words text-sm text-danger-500"
              data-testid="login-error"
            >
              {failure}
            </p>
          ) : null}

          <div className="mt-6 grid gap-2 border-ink-200 border-t pt-6">
            {telegramOidc ? (
              <button
                type="button"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-ink-200 px-4 font-semibold text-ink-800 text-sm transition-colors hover:border-primary/30 hover:bg-accent hover:text-primary disabled:opacity-60"
                data-testid="oidc-login-button"
                disabled={telegramPending}
                onClick={() => void startTelegram()}
              >
                <GraduationCap size={18} aria-hidden="true" />
                {telegramPending ? "Открываем Telegram…" : "Инструктор — войти через Telegram"}
              </button>
            ) : telegramBot ? (
              <>
                {/*
                  Ссылка на бота, а не `?startapp`: Main Mini App в BotFather не заведён, кабинет
                  открывается кнопкой меню в чате. Пока не заданы TELEGRAM_OIDC_* — это весь путь
                  инструктора на сайте.
                */}
                <a
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-ink-200 px-4 font-semibold text-ink-800 text-sm transition-colors hover:border-primary/30 hover:bg-accent hover:text-primary"
                  href={`https://t.me/${telegramBot}`}
                  data-testid="telegram-miniapp-link"
                >
                  <GraduationCap size={18} aria-hidden="true" />
                  Инструктор — открыть бота в Telegram
                </a>
                <p className="text-ink-600 text-xs">
                  В чате с ботом нажмите кнопку меню — она откроет кабинет.
                </p>
              </>
            ) : (
              <p className="text-ink-600 text-xs" data-testid="telegram-unconfigured">
                Вход инструктора через Telegram на этом стенде не настроен.
              </p>
            )}
            {amoWidget ? (
              <p className="text-ink-600 text-xs">
                Менеджер может войти и из карточки сделки в amoCRM — кнопкой «Открыть в отдельной
                вкладке»; вход держится месяц.
              </p>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}
